import React, { useState } from 'react'
import { Loader2Icon } from 'lucide-react'
import clsx from 'clsx'

import DataTableLoader from '../../pages/myEvent/component/DataTableLoader'
import NoDataAvailable from '../../pages/myEvent/component/NoDataAvailable'
import InfiniteScrollDataTable from '../common/tables/InfiniteScrollDataTable'
import useFinances from '../../hooks/useFinances'
import { Button } from '../ui/button'
import { CurrencyTooltip } from '../user/views/profile/components/CurrencyTooltip'

import { cn } from '../../lib/utils'

import { useAppSelector } from '../../store/hooks'
import { selectConnectAllPayouts } from '../../store/finances/financesSlice'

import { IConnectAllPayout } from '../user/views/profile/Tabs/profile-root-page-finance-tab/ProfileRootPageFinanceTab'

const NO_EVENTS_IMAGE_BLANK = 'assets/placeholders/insuficientFund.svg'

type Props = {
  connectAccountDetail?: any
  fetMoreData?: any
  hasMore: boolean
  loading?: boolean
  handleModal?: any
  stripeConnectAccountId?: string
  currencyCode?: string
}

const OrganizerStripeTransactions = ({
  hasMore,
  loading,
  fetMoreData,
  handleModal,
  connectAccountDetail,
  stripeConnectAccountId,
  currencyCode,
}: Props) => {
  const [transactionLoading, setTransactionLoading] = useState('')
  const { onCancelPayout, getTransactions } = useFinances()

  const connectAllPayouts = useAppSelector(selectConnectAllPayouts)

  const cols = [
    {
      name: 'Amount',
      cell: (row: IConnectAllPayout) => {
        return (
          <div className="text-SeabiscuitDark200ThemeColor text-center flex items-center">
            <img src="/assets/og_icons/Money Bag-2.svg" className="mr-4 w-6" alt="moneyBag" />
            <span className="mr-2">
              <CurrencyTooltip currencyCode={currencyCode} /> ${row.amount / 100}
            </span>{' '}
            transferred
          </div>
        )
      },
      draggable: false,
      width: '300px',
    },
    {
      name: 'Paid on',
      cell: (row: IConnectAllPayout) => {
        const date = new Date(row.created * 1000)

        const day = date.getDate()
        const dayWithSuffix =
          day +
          (day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
              ? 'nd'
              : day % 10 === 3 && day !== 13
                ? 'rd'
                : 'th')

        const formattedDateWithSuffix =
          date.toLocaleString('en-US', { month: 'long' }) +
          ' ' +
          dayWithSuffix +
          ', ' +
          date.getFullYear()

        return (
          <div className="text-SeabiscuitDark200ThemeColor text-center flex items-center">
            <img src="/assets/og_icons/Tear-OffCalendar.svg" className="mr-4 w-6" alt="calender" />
            {formattedDateWithSuffix}
          </div>
        )
      },
      draggable: false,
      width: '300px',
    },
    {
      name: 'Account Number',
      cell: (row: IConnectAllPayout) => {
        return (
          <div className="text-SeabiscuitDark200ThemeColor flex items-center">
            <img src="/assets/og_icons/Museum2.svg" className="mr-4 w-6" alt="moneyBag" />
            Account ending in: ****{row.accountNumber}
          </div>
        )
      },
      draggable: false,
      width: '340px',
    },
    {
      name: 'Transaction Id',
      cell: (row: IConnectAllPayout) => {
        return (
          <div className="text-SeabiscuitDark200ThemeColor flex items-center">
            <img src="/assets/og_icons/Fingerprint.svg" className="mr-4 w-6" alt="moneyBag" />
            {row.statement_descriptor}
          </div>
        )
      },
      draggable: false,
      width: '320px',
    },
    {
      name: 'Action',
      cell: (row: IConnectAllPayout) => {
        return (
          <div className="flex items-center justify-end w-full">
            <Button
              disabled={transactionLoading === row.id}
              variant="link"
              className={cn(
                'p-0 text-sm text-SeabiscuitMainThemeColor underline hover:no-underline',
                row.status !== 'pending' && 'hidden'
              )}
              onClick={async () => {
                setTransactionLoading(row.id)
                await onCancelPayout(row.id, stripeConnectAccountId || '')
                await getTransactions()
                setTransactionLoading('')
              }}
            >
              {transactionLoading === row.id && <Loader2Icon className="animate-spin w-4" />}
              Cancel
            </Button>
          </div>
        )
      },
      draggable: false,
      minWidth: '150px',
    },
  ]

  return (
    <span className="overflow-auto">
      <InfiniteScrollDataTable
        fetchMore={fetMoreData}
        columns={cols}
        hasMore={hasMore}
        fixedHeader={true}
        noTableHead={true}
        data={connectAllPayouts}
        progressPending={loading}
        noDataComponent={
          <NoDataAvailable
            handleModal={handleModal}
            data={
              connectAccountDetail.length && {
                last4: connectAccountDetail[0]?.last4,
                stripeConnectAccountId,
              }
            }
            imgSrc={NO_EVENTS_IMAGE_BLANK}
            bottomText={'Make your first transfer now >'}
            text={'You have not transferred any funds '}
          />
        }
        className={clsx('transition-all', connectAllPayouts?.length && '')}
        progressComponent={
          <div className="md:h-[300px] 2xl:h-[500px] flex items-center 12312">
            <DataTableLoader />
          </div>
        }
        noHeader={false}
      />
    </span>
  )
}

export default OrganizerStripeTransactions
